import { useRef, useState } from "react";
import axios from "../../services/axios/backendAxios";
// import { User } from "../../types";
import SubtleButton from "../../components/Buttons/SubtleButton";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { invalidateQueriesContaining } from "../../utils/queryInvalidation";
import flash from "../../services/flash";
import errors from "../../services/errors";
import { UserSelect } from "./TransferOwnership";
import { Account, User } from "@mysnowscape/snow-lib";
import { useDispatch } from "react-redux";
import { updateUser } from "../../store/reducers/currentUserSlice";
import { useTranslation } from "react-i18next";
type FormPayload = {
  user_id: string;
};
export default function UserInfo() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [isEditingPhone, setIsEditingPhone] = useState(false);
  const [newPhoneInput, setNewPhoneInput] = useState("");
  const allowedDevicesDebounce = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );
  const accountsQuery = useQuery<Account[]>({
    queryKey: ["account", `user-${selectedUser?._id}`],
    queryFn: async () => {
      const resp = await axios.get(
        "/account?ownerId=" + selectedUser?._id.toString(),
      );
      return resp.data.accounts;
    },
    enabled: selectedUser != null,
  });
  const updateStripeInfo = useMutation({
    mutationFn: (data: FormPayload) => {
      return axios.post(`user/developer/update-stripe-info`, data);
    },
    onSuccess: (resp) => {
      flash.success(resp?.data?.message ?? t("userInfo.stripeInfoUpdated"));
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        predicate: (query) =>
          query.queryKey.find(
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            (key: any) =>
              typeof key === "string" &&
              (key.includes("company") ||
                key.includes("companies") ||
                key.includes("in-good-standing") ||
                key.includes("account") ||
                key.includes("user")),
          ) !== undefined,
      });
    },
    onError: (err) => {
      flash.error(t("common.error", { message: err.message ?? err }));
      errors.report(err);
    },
  });
  const unlockAccount = useMutation({
    mutationFn: (accountId: string) => {
      return axios.post(`account/${accountId}/unlock`);
    },
    onSuccess: (resp) => {
      flash.success(resp?.data?.message ?? t("userInfo.accountUnlocked"));
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        predicate: (query) =>
          query.queryKey.find(
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            (key: any) =>
              key.includes("company") ||
              key.includes("companies") ||
              key.includes("in-good-standing") ||
              key.includes("account") ||
              key.includes("user"),
          ) !== undefined,
      });
    },
    onError: (err) => {
      flash.error(t("common.error", { message: err.message ?? err }));
      errors.report(err);
    },
  });
  const updateAllowedDevices = useMutation({
    mutationFn: (allowedDevices: number) => {
      return axios.post(`user/${selectedUser!.uid}/update`, { allowedDevices });
    },
    onSuccess: (resp) => {
      setSelectedUser(resp.data);
      flash.success(t("userInfo.allowedDevicesUpdated"));
    },
    onError: (err) => {
      flash.error(t("common.error", { message: err.message ?? err }));
      errors.report(err);
    },
  });

  const changePhoneNumberMutation = useMutation({
    mutationFn: (newPhone: string) => {
      return axios.post(`user/developer/change-phone-number`, {
        user_id: selectedUser!._id.toString(),
        newPhoneNumber: newPhone,
      });
    },
    onSuccess: (resp) => {
      flash.success(resp?.data?.message ?? t("userInfo.phoneNumberUpdated"));
      setSelectedUser(
        resp?.data?.user ?? {
          ...selectedUser!,
          phoneNumber: resp?.data?.newPhoneNumber,
        },
      );
      setIsEditingPhone(false);
      setNewPhoneInput("");
    },
    onSettled: () => {
      invalidateQueriesContaining(queryClient, [
        "company",
        "companies",
        "user",
      ]);
    },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onError: (err: any) => {
      const msg =
        err.response?.data?.message ??
        err.message ??
        t("userInfo.errorChangingPhone");
      flash.error(t("common.error", { message: msg }));
      errors.report(err);
    },
  });

  const impersonate = async (phoneNumber: string) => {
    try {
      await axios.post("/user/impersonate", { phone: phoneNumber });
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (err: any) {
      if (err.response?.data?.message) {
        flash.error(err.response.data.message);
      }
    }
    let userResponse = await axios.get("/user/me");
    await dispatch({ type: "SIGNOUT_CLEAR_STORE" });
    await dispatch(
      updateUser({
        ...userResponse.data.user,
        is_impersonating: userResponse.data.is_impersonating ?? false,
      }),
    );
    // window.location.reload();
  };

  return (
    <div>
      <h2 className="text-lg font-bold">{t("userInfo.toolsTitle")}</h2>
      <p className="text-xs text-gray-700">{t("userInfo.toolsDescription")}</p>
      <form className="grid grid-cols-1 gap-2">
        <label>
          <span className="text-xs text-gray-500">{t("userInfo.user")}</span>
          <UserSelect
            name="user_id"
            onChange={(user) => {
              setSelectedUser(user);
              setIsEditingPhone(false);
              setNewPhoneInput("");
            }}
          />
        </label>
        <h2 className="font-bold">{t("userInfo.userDetails")}</h2>
        <table>
          <tr>
            <td>{t("userInfo.databaseId")}</td>
            <td>{selectedUser?._id.toString()}</td>
          </tr>
          <tr>
            <td>{t("userInfo.firebaseId")}</td>
            <td>{selectedUser?.uid}</td>
          </tr>
          <tr>
            <td>{t("userInfo.phoneNumber")}</td>
            <td>
              {selectedUser && (
                <div className="py-1">
                  <span className="font-mono text-sm mr-2">
                    {selectedUser.phoneNumber}
                  </span>
                  {!isEditingPhone ? (
                    <button
                      type="button"
                      className="text-xs text-blue-600 underline font-semibold"
                      onClick={() => {
                        setNewPhoneInput(selectedUser.phoneNumber || "");
                        setIsEditingPhone(true);
                      }}
                    >
                      {t("common.change")}
                    </button>
                  ) : (
                    <div className="mt-1 flex flex-col gap-1 border p-2 rounded bg-gray-50 max-w-sm">
                      <span className="text-xs text-gray-700 font-semibold">
                        {t("userInfo.newPhoneNumberLabel")}
                      </span>
                      <div className="flex gap-1 items-center">
                        <input
                          type="tel"
                          placeholder="+17125550100"
                          value={newPhoneInput}
                          onChange={(e) => setNewPhoneInput(e.target.value)}
                          className="border rounded px-2 py-1 text-sm flex-1"
                        />
                        <SubtleButton
                          type="button"
                          loading={changePhoneNumberMutation.isPending}
                          disabled={!newPhoneInput.trim()}
                          onClick={() => {
                            if (
                              confirm(
                                t("userInfo.confirmChangePhone", {
                                  firstName: selectedUser.firstName,
                                  phone: newPhoneInput,
                                }),
                              )
                            ) {
                              changePhoneNumberMutation.mutate(newPhoneInput);
                            }
                          }}
                        >
                          {t("common.save")}
                        </SubtleButton>
                        <button
                          type="button"
                          className="text-xs text-gray-500 ml-1 hover:underline"
                          onClick={() => {
                            setIsEditingPhone(false);
                            setNewPhoneInput("");
                          }}
                        >
                          {t("common.cancel")}
                        </button>
                      </div>
                      <span className="text-[10px] text-gray-500 leading-tight">
                        {t("userInfo.phoneChangeHint")}
                      </span>
                    </div>
                  )}
                </div>
              )}
            </td>
          </tr>
          <tr>
            <td>{t("userInfo.email")}</td>
            <td>{selectedUser?.email}</td>
          </tr>
          <tr>
            <td>{t("userInfo.allowedDevices")}</td>
            <td>
              {selectedUser && (
                <input
                  key={selectedUser?._id?.toString()}
                  type="number"
                  min={1}
                  disabled={
                    selectedUser === null || updateAllowedDevices.isPending
                  }
                  defaultValue={selectedUser?.allowedDevices ?? 1}
                  onChange={(e) => {
                    const val = Number(e.target.value);
                    if (allowedDevicesDebounce.current)
                      clearTimeout(allowedDevicesDebounce.current);
                    allowedDevicesDebounce.current = setTimeout(() => {
                      if (
                        selectedUser &&
                        val >= 1 &&
                        val !== (selectedUser.allowedDevices ?? 1)
                      ) {
                        updateAllowedDevices.mutate(val);
                      }
                    }, 600);
                  }}
                  onBlur={(e) => {
                    if (allowedDevicesDebounce.current)
                      clearTimeout(allowedDevicesDebounce.current);
                    const val = Number(e.target.value);
                    if (
                      selectedUser &&
                      val !== (selectedUser.allowedDevices ?? 1)
                    ) {
                      updateAllowedDevices.mutate(val);
                    }
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      if (allowedDevicesDebounce.current)
                        clearTimeout(allowedDevicesDebounce.current);
                      const val = Number((e.target as HTMLInputElement).value);
                      if (
                        selectedUser &&
                        val !== (selectedUser.allowedDevices ?? 1)
                      ) {
                        updateAllowedDevices.mutate(val);
                      }
                    }
                  }}
                  className="border rounded px-1 w-16 text-sm"
                />
              )}
            </td>
          </tr>
          <tr>
            <td>{t("userInfo.errorReports")}</td>
            <td>
              {selectedUser && (
                <>
                  <a
                    className="underline mr-2"
                    href={`https://app.honeybadger.io/projects/117723/faults?sort=last_seen_desc&q=context.user_id%3A%22${selectedUser?.uid}%22%E2%80%81-is%3Aresolved%E2%80%81-is%3Aignored%E2%80%81`}
                  >
                    {t("userInfo.reportsWeb")}
                  </a>
                  <a
                    className="underline mr-2"
                    href={`https://app.honeybadger.io/projects/115977/faults?sort=last_seen_desc&q=context.user_id%3A%22${selectedUser?.uid}%22%E2%80%81-is%3Aresolved%E2%80%81-is%3Aignored%E2%80%81`}
                  >
                    {t("userInfo.reportsBackend")}
                  </a>
                  <a
                    className="underline mr-2"
                    href={`https://app.honeybadger.io/projects/116062/faults?sort=last_seen_desc&q=context.user_id%3A%22${selectedUser?.uid}%22%E2%80%81-is%3Aresolved%E2%80%81-is%3Aignored%E2%80%81`}
                  >
                    {t("userInfo.reportsApp")}
                  </a>
                </>
              )}
            </td>
          </tr>
        </table>
        <h2 className="font-bold">{t("userInfo.userAccounts")}</h2>
        <table>
          {accountsQuery.data?.map((a) => (
            <tr key={a._id.toString()}>
              <td>{a._id.toString()}</td>
              <td>
                {a.locked ? (
                  <>
                    <div className="text-snow-red">
                      {t("userInfo.locked", {
                        reason: a.lockedMessage ?? t("userInfo.noReasonGiven"),
                      })}
                    </div>
                    <SubtleButton
                      type="button"
                      loading={unlockAccount.isPending}
                      onClick={() => {
                        unlockAccount.mutate(a._id.toString());
                      }}
                    >
                      {t("userInfo.unlock")}
                    </SubtleButton>
                  </>
                ) : (
                  <span>{t("userInfo.ok")}</span>
                )}
              </td>
            </tr>
          ))}
        </table>
        <div className="actions flex gap-2">
          <SubtleButton
            disabled={selectedUser === null}
            type="button"
            className="mt-2"
            loading={updateStripeInfo.isPending}
            onClick={() => {
              updateStripeInfo.mutate({
                user_id: selectedUser!._id,
              });
            }}
          >
            {t("userInfo.updateStripeInfo")}
          </SubtleButton>
          <SubtleButton
            disabled={selectedUser === null}
            type="button"
            className="mt-2"
            onClick={() => {
              impersonate(selectedUser!.phoneNumber);
            }}
          >
            {t("userInfo.loginAsUser")}
          </SubtleButton>
        </div>
      </form>
    </div>
  );
}
